import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ── Sending a document to your own customer ────────────────────────────────
//
// The free tool, from the technician's side. He has written an estimate; this
// is the sheet that asks who it is for and hands him back a link.
//
// ── WHY THE LINK IS AS IMPORTANT AS THE EMAIL ────────────────────────────
// Half the homeowners in Utah County give a plumber a phone number and no
// email address. If this only knew how to email, half the estimates written
// here would have nowhere to go. So the link is always produced, always shown,
// always copyable, and the email is the optional extra. The server works the
// same way round: the document is shared the moment it has a token, and a
// failed email is reported as "send it yourself", not as a failure.
//
// ── THE NUMBERS ON IT ARE HIS, NOT QPS'S ─────────────────────────────────
// Nothing on the customer's copy comes from QPS — not the markup, not the
// margin, not the approval. See server/share.js.

const digits = (v) => String(v || '').replace(/[^\d+]/g, '');

export default function SendToCustomer({ kind, doc, authHeaders, onClose, onSent }) {
  const label = kind === 'invoice' ? 'invoice' : 'estimate';
  const existing = doc?.customer || {};

  const [problem, setProblem] = useState('');
  const [f, setF] = useState({
    customerName: existing.name || '',
    customerEmail: existing.email || '',
    customerPhone: existing.phone || '',
    message: '',
  });
  // Typing clears the complaint. Leaving "Put their name on it" on screen
  // under a filled-in name field is how somebody taps Send twice and decides
  // the button is broken.
  const set = (patch) => { setProblem(''); setF((p) => ({ ...p, ...patch })); };

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);   // { link, emailed, message }
  const [copied, setCopied] = useState(false);

  // Slide in rather than appear, the same as every other sheet in the portal.
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShown(true), 10); return () => clearTimeout(t); }, []);

  const send = async () => {
    setProblem('');
    if (!f.customerName.trim()) { setProblem('Put their name on it.'); return; }
    setSending(true);
    try {
      const base = kind === 'invoice' ? '/api/ledger' : '/api/estimates';
      const res = await axios.post(`${base}/${doc._id}/send`, f, authHeaders());
      setResult(res.data);
      onSent?.(res.data);
    } catch (err) {
      setProblem(err.response?.data?.message || err.message || 'That did not send. Try once more.');
    } finally {
      setSending(false);
    }
  };

  // The clipboard API needs a secure context and a user gesture, and still
  // refuses on some in-app browsers. Selecting the text is the fallback that
  // has always worked, so the link sits in a real input rather than a div.
  const copy = async () => {
    const input = document.getElementById('stc-link');
    try {
      await navigator.clipboard.writeText(result.link);
    } catch {
      input?.select();
      try { document.execCommand('copy'); } catch { /* they can copy it by hand */ }
    }
    input?.select();
    setCopied(true);
    setTimeout(() => setCopied(false), 2400);
  };

  const smsHref = () => {
    const to = digits(f.customerPhone);
    const body = encodeURIComponent(
      `Here is your ${label} from us: ${result.link}`
    );
    // The separator differs by platform and both are tolerated widely enough.
    return `sms:${to}${/iPhone|iPad|Mac/.test(navigator.userAgent) ? '&' : '?'}body=${body}`;
  };

  return (
    <div className={`modal${shown ? ' active' : ''} stc`} role="dialog" aria-modal="true">
      <div className="stc-scrim" onClick={onClose} />
      <div className="modal-box stc-box">
        <div className="modal-handle" />

        {!result ? (
          <>
            <h2 className="stc-h">Send this {label} to your customer</h2>
            <p className="stc-sub">
              They get a link with your business name on it{kind === 'invoice'
                ? '. No account, nothing to sign up for.'
                : ', and they can approve it right there with their finger.'}
            </p>

            <div className="form-group">
              <label>Customer name <span className="req">*</span></label>
              <input value={f.customerName} onChange={e => set({ customerName: e.target.value })}
                placeholder="Who is this for?" autoComplete="off" />
            </div>

            <div className="form-group">
              <label>Their email <span className="stc-opt">(if you have it)</span></label>
              <input type="email" value={f.customerEmail} inputMode="email"
                onChange={e => set({ customerEmail: e.target.value })}
                placeholder="name@example.com" autoComplete="off" />
            </div>

            <div className="form-group">
              <label>Their phone <span className="stc-opt">(to text them the link)</span></label>
              <input type="tel" value={f.customerPhone} inputMode="tel"
                onChange={e => set({ customerPhone: e.target.value })}
                placeholder="(801) 555-0100" autoComplete="off" />
            </div>

            <div className="form-group">
              <label>A note for them <span className="stc-opt">(optional)</span></label>
              <textarea rows={3} value={f.message} onChange={e => set({ message: e.target.value })}
                placeholder="Thanks for having me out today. Here is the price we talked about." />
            </div>

            {problem && <div className="stc-problem">{problem}</div>}

            <button type="button" className="stc-send" onClick={send} disabled={sending}>
              {sending ? 'Sending…' : `Send ${label}`}
            </button>
            <button type="button" className="stc-cancel" onClick={onClose}>Not now</button>
          </>
        ) : (
          <>
            <div className="stc-tick">✓</div>
            <h2 className="stc-h center">{result.emailed ? 'Sent' : 'Ready to send'}</h2>
            <p className="stc-sub center">{result.message}</p>

            <label className="stc-link-l">Their link</label>
            <input id="stc-link" className="stc-link" readOnly value={result.link}
              onFocus={e => e.target.select()} />

            <button type="button" className="stc-send" onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </button>

            {digits(f.customerPhone).length >= 10 && (
              // Not "Text it to Mrs" — a first name split off "Mrs Henderson"
              // is a title, and getting somebody's name wrong on the button
              // that sends them their quote is a bad place to be clever.
              <a className="stc-second" href={smsHref()}>Text them the link</a>
            )}

            <p className="stc-keep">
              This link keeps working. Send the {label} again and they get the same one,
              so a customer who comes back to it next week still finds it.
            </p>

            <button type="button" className="stc-cancel" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}
